import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AtlasSdk from '@atlas/sdk';

import { useTheme } from '@cobalt/react-theme-provider';
import Flex from '@cobalt/react-flex';
import { Text } from '@cobalt/react-typography';
import Tag from '@cobalt/react-tag';
import Button from '@cobalt/react-button';
import LabeledIcon from '@cobalt-marketplace/react-labeled-icon';
import Tooltip from '@cobalt-marketplace/react-tooltip';
import Icon from '@cobalt/react-icon';

import { ModalPro as Modal, Dropdown } from '@case-management/components';

import { ColumnType } from '@/components/table';

import { useWebFormConfig } from './use-web-form-config';
import { useDeleteWebFormConfig, useListWebFormConfig } from './use-web-form-config-api';

import { WebFormConfigModel, WebformLanguageMapper } from '@/models/web-form.model';

import { history } from '@/config/routeAtlas';
import { formatDateToCalendar } from '@/utils/helper';
import { webformsCls } from '@/config/pendo.variable';

export function useWebFormConfigListPage() {
  const { t } = useTranslation();
  const theme = useTheme();

  const { handleGoToPage, handleOpenPreviewModal, handleOpenCopyModal } = useWebFormConfig();
  const {
    data: webFormConfigList,
    loading: webFormConfigListLoading,
    run: getWebFormConfigList,
    cancel: cancelWebFormConfigList,
  } = useListWebFormConfig();
  const { run: deleteWebFormConfig } = useDeleteWebFormConfig();

  const tableColumns: Array<ColumnType<WebFormConfigModel>> = [
    {
      title: t('webform_config.hosting_website'),
      key: 'website_url',
      render({ website_url }: WebFormConfigModel) {
        return (
          <Text truncated title={website_url}>
            {website_url}
          </Text>
        );
      },
    },
    {
      title: t('webform_config.case_forms'),
      key: 'case_forms',
      render({ data_list }: WebFormConfigModel) {
        const form_names = data_list.map(({ form_name }) => form_name).join(' ');
        return (
          <Text truncated title={form_names}>
            {form_names}
          </Text>
        );
      },
    },
    {
      title: t('webform_config.language'),
      key: 'language',
      render({ data_list }: WebFormConfigModel) {
        const language = data_list[0]?.language;
        if (!language) return;

        const lng = WebformLanguageMapper[language].lng;
        return (
          <Text truncated title={lng}>
            {lng}
          </Text>
        );
      },
    },
    {
      title: t('webform_config.touchpoint'),
      key: 'touchpoint',
      render({ data_list }: WebFormConfigModel) {
        const touchpoint = data_list[0]?.touchpoint;
        const touchpoint_name = data_list[0]?.touchpoint_name;

        if (touchpoint_name && touchpoint) {
          const text = `${touchpoint_name} <${touchpoint}>`;
          return (
            <Text truncated title={text}>
              {text}
            </Text>
          );
        } else if (touchpoint) {
          return (
            <Text truncated title={touchpoint}>
              {touchpoint}
            </Text>
          );
        }
      },
    },
    {
      title: t('common.created'),
      key: 'create_at',
      render(item: WebFormConfigModel) {
        const create_at = formatDateToCalendar(item.create_at);
        return (
          <Text truncated title={create_at}>
            {create_at}
          </Text>
        );
      },
    },
    {
      title: t('webform_config.status'),
      key: 'status',
      render(item: WebFormConfigModel) {
        if (item.status) {
          return (
            <Tag size="small" color="light-green">
              {t('webform_config.available')}
            </Tag>
          );
        } else {
          return (
            <Tag style={{ color: '#475059', backgroundColor: '#E8EAEB' }} size="small">
              {t('webform_config.unavailable')}
            </Tag>
          );
        }
      },
    },
    {
      title: '',
      key: 'operation',
      render(item: WebFormConfigModel) {
        const form_id = item.data_list[0]?.form_id;
        const language = item.data_list[0]?.language;
        const overlay = (
          <>
            <Button
              data-bi={`${webformsCls}-operation-edit`}
              key="edit"
              type="secondary"
              shape="full-width"
              variation="transparent"
              onClick={() => handleEdit(item.web_form_key)}
            >
              <Flex alignY="center" width="100%">
                <LabeledIcon label={t('common.edit')} iconName="edit" iconSize="tiny" />
              </Flex>
            </Button>

            <Button
              data-bi={`${webformsCls}-operation-delete`}
              key="delete"
              type="danger"
              shape="full-width"
              variation="transparent"
              onClick={() => handleDelete(item.web_form_key)}
            >
              <Flex alignY="center" width="100%">
                <LabeledIcon label={t('common.delete')} iconName="delete_outline" iconSize="tiny" />
              </Flex>
            </Button>
          </>
        );

        return (
          <Flex alignX="end" gap="1">
            {!!(form_id && language) && (
              <Tooltip variation="dark" label={t('webform_config.preview')}>
                <Button
                  data-bi={`${webformsCls}-preview-webform-icon`}
                  size="small"
                  type="secondary"
                  shape="compact"
                  variation="transparent"
                  onClick={() => handleOpenPreviewModal(form_id, language)}
                >
                  <Icon name="visibility" size="tiny" />
                </Button>
              </Tooltip>
            )}

            <Tooltip variation="dark" label={t('webform_config.copy.title')}>
              <Button
                data-bi={`${webformsCls}-html-code-icon`}
                size="small"
                type="secondary"
                shape="compact"
                variation="transparent"
                onClick={() => handleOpenCopyModal(item)}
              >
                <Icon name="code" size="tiny" />
              </Button>
            </Tooltip>

            <Dropdown
              btnSize="small"
              btnVariation="transparent"
              shape="compact"
              origin="SE"
              direction="SW"
              overlay={overlay}
            >
              <Icon name="more_horiz" size="small" />
            </Dropdown>
          </Flex>
        );
      },
    },
  ];

  const showWebFormTable = useMemo(() => {
    return webFormConfigListLoading || webFormConfigList.length > 0;
  }, [webFormConfigList.length, webFormConfigListLoading]);

  const handleEdit = (web_form_key: string) => {
    handleGoToPage('edit', { web_form_key });
  };

  const handleConfirmDelete = async (id: string) => {
    try {
      const result = await deleteWebFormConfig(id);
      if (!result) return;

      AtlasSdk.toast.show({
        title: t('common.message'),
        message: t('webform_config.delete.success'),
        type: AtlasSdk.toast.types.SUCCESS,
        dismissible: true,
        dismissDelay: AtlasSdk.toast.delays.SHORT,
      });

      init();
    } catch (error) {}
  };

  const handleDelete = (web_form_key: string) => {
    Modal.confirm({
      icon: <Icon name="delete_outline" size="large" color={theme.gray500} />,
      title: t('webform_config.delete.modal.title'),
      content: t('webform_config.delete.modal.content'),
      okType: 'danger',
      okText: t('common.delete_permanently'),
      cancelText: t('common.cancel'),
      onOk: () => handleConfirmDelete(web_form_key),
    });
  };

  const autoPopupCopyModal = async () => {
    const state = history.location.state as WebFormConfigModel | undefined;
    history.location.state = undefined;
    if (!state?.web_form_key) return;
    await handleOpenCopyModal(state);
  };

  const init = async () => {
    try {
      await getWebFormConfigList({ page: 1, per_page: 999 });
    } catch (error) {}
  };

  useEffect(() => {
    init();
    autoPopupCopyModal();

    return () => {
      cancelWebFormConfigList();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    tableColumns,
    webFormConfigList,
    webFormConfigListLoading,
    showWebFormTable,
  };
}
